import { catchWithLog } from 'helper/zod';
import { z } from 'zod';

import type { MissionEventType, MissionV2, VehicleV2 } from 'API';
import { ErrorTypeV2 } from 'API';
import {
    AdsStateFieldV2Schema,
    ConnectivityStatusFieldV2Schema,
    DeviationsFieldV2Schema,
    DriverModeFieldV2Schema,
    DrivingStateFieldV2Schema,
    MissionEventSchema,
    MissionProgressDetailsFieldV2Schema,
    MissionStateFieldV2Schema,
    MissionV2Schema,
    MrmStateFieldV2Schema,
    PositionFieldV2Schema,
    SpeedMsFieldV2Schema,
    VehicleV2Schema,
} from 'APIzod';

/**
 * Utility type that enforces all keys of T are explicitly provided in a zod object shape,
 * preventing accidental omission of fields when constructing safe schema overrides.
 */
type ExactShape<T> = {
    [K in keyof T]-?: z.ZodType<T[K], any, any>;
};

/**
 * Builds a synthetic ErrorV2 with type PARSING_ERROR, used as a fallback when
 * client-side zod parsing fails on a V2 field value. This allows consumers to
 * distinguish a field that failed to parse from one that simply has no value.
 */
const parsingError = () => ({
    __typename: 'ErrorV2' as const,
    raisedAt: new Date().toISOString(),
    type: ErrorTypeV2.PARSING_ERROR,
});

const fieldParsingError = () => ({ __typename: undefined, value: null, error: parsingError(), ttl: null });

/**
 * Wraps a V2 field schema so that when the value fails to parse, the field is
 * preserved with value: null and a synthetic PARSING_ERROR injected into error.
 * If the entire field object is structurally broken, also returns a field with
 * value: null and PARSING_ERROR rather than null.
 */
const makeSafeFieldSchema = <
    T extends {
        __typename: z.ZodType<any>;
        error: z.ZodType<any>;
        ttl: z.ZodType<any>;
        value: z.ZodType<any>;
    },
>(
    shape: T,
    label: string
) => {
    const CATCH_SENTINEL = Symbol('catch');
    return z
        .object({
            __typename: shape.__typename,
            error: shape.error,
            ttl: shape.ttl,
            value: catchWithLog(shape.value, CATCH_SENTINEL as any, label),
        })
        .transform(field => {
            const wasCaught = field.value === CATCH_SENTINEL;
            return {
                ...field,
                value: wasCaught ? null : field.value,
                error: field.error ?? (wasCaught ? parsingError() : null),
            };
        });
};

/**
 * Sentinel used when the backend sends a mission event type this build does not
 * know yet. Mission events are rendered as a log, so an unrecognised entry is
 * kept and labelled "Unknown" rather than dropped — the surrounding entries
 * still carry meaning, and losing them would hide mission history.
 *
 * TODO: REST-SUBSCRIPTIONS: this is a patch, not the final design. It stops one
 * known trigger (an unknown event type) from destroying the whole mission, but the
 * mission still has no safe mapping layer of its own.
 * How to fix when missions move to REST:
 * 1. Write a mission mapper next to `domain/mappers/vehicleFromRestV2.ts` and copy how
 *    it already solves this problem.
 * 2. Use its `fromEnum` helper: it turns any string it does not recognise into
 *    'UNKNOWN' and returns a value instead of throwing.
 * 3. Keep the list of known values written by hand in the mapper, like `MRM_STATES`
 *    there, and always include 'UNKNOWN'. Never build it from generated code,
 *    otherwise the next codegen run can silently change what your app accepts.
 * 4. Once that mapper exists, delete this sentinel and this file's mission part, and
 *    point MissionEventLogDisplayValue at the domain type instead.
 */
export const UNKNOWN_MISSION_EVENT_TYPE = 'UNKNOWN';

export type MissionEventTypeOrUnknown = MissionEventType | typeof UNKNOWN_MISSION_EVENT_TYPE;

/**
 * Safe version of MissionEventSchema.
 * An unrecognised `type` degrades to UNKNOWN_MISSION_EVENT_TYPE instead of failing
 * the event — and, because `events` is a required part of the mission object, the
 * whole mission along with it.
 */
const SafeMissionEventSchema = () => {
    const shape = MissionEventSchema().shape;
    return z.object({
        ...shape,
        type: catchWithLog(
            z.union([shape.type, z.literal(UNKNOWN_MISSION_EVENT_TYPE)]),
            UNKNOWN_MISSION_EVENT_TYPE,
            'MissionV2.events[].type'
        ),
    });
};

/**
 * Safe version of the mission `events` list.
 * Entries are validated one by one, so a structurally broken event (bad timestamp,
 * missing position) is dropped on its own instead of taking the rest of the log —
 * or the mission — with it. A broken list as a whole degrades to null.
 *
 * The cast is required because the widened `type` no longer matches MissionEvent
 * exactly. MissionEventLogDisplayValue accepts MissionEventTypeOrUnknown to cover
 * the widened value at the point where it is actually read.
 */
const SafeMissionEventsSchema = () =>
    catchWithLog(
        z
            .array(catchWithLog(SafeMissionEventSchema().nullable(), null, 'MissionV2.events[]'))
            .transform(entries => entries.filter(entry => entry !== null))
            .nullish(),
        null,
        'MissionV2.events'
    ) as unknown as z.ZodType<MissionV2['events']>;

// Safe top-level schemas — optional fields degrade gracefully on parse failure:
// the field is preserved with value: null and error.type = PARSING_ERROR injected,
// so consumers can distinguish a parse failure from a field with no value.

/**
 * Safe version of VehicleV2Schema.
 * Each optional field uses makeSafeFieldSchema so that a parse failure degrades
 * the value to null and injects a PARSING_ERROR, without affecting other fields
 * or other vehicles in the list.
 */
export const SafeVehicleV2Schema = (): z.ZodType<VehicleV2> => {
    const shape = VehicleV2Schema().shape;
    const safeShape: ExactShape<VehicleV2> = {
        __typename: shape.__typename,
        vin: shape.vin,
        fleetId: shape.fleetId,
        licensePlate: shape.licensePlate,
        name: shape.name,
        timestamp: shape.timestamp,
        latestVehicleUpdate: shape.latestVehicleUpdate,
        owners: shape.owners,
        activeMissionId: shape.activeMissionId,
        adsState: catchWithLog(
            makeSafeFieldSchema(AdsStateFieldV2Schema().shape, 'VehicleV2.adsState').nullish(),
            fieldParsingError(),
            'VehicleV2.adsState'
        ),
        connectivityStatus: catchWithLog(
            makeSafeFieldSchema(ConnectivityStatusFieldV2Schema().shape, 'VehicleV2.connectivityStatus').nullish(),
            fieldParsingError(),
            'VehicleV2.connectivityStatus'
        ),
        deviations: z
            .array(makeSafeFieldSchema(DeviationsFieldV2Schema().shape, 'VehicleV2.deviations[]').nullable())
            .nullish(),
        driverMode: catchWithLog(
            makeSafeFieldSchema(DriverModeFieldV2Schema().shape, 'VehicleV2.driverMode').nullish(),
            fieldParsingError(),
            'VehicleV2.driverMode'
        ),
        drivingState: catchWithLog(
            makeSafeFieldSchema(DrivingStateFieldV2Schema().shape, 'VehicleV2.drivingState').nullish(),
            fieldParsingError(),
            'VehicleV2.drivingState'
        ),
        mrmState: catchWithLog(
            makeSafeFieldSchema(MrmStateFieldV2Schema().shape, 'VehicleV2.mrmState').nullish(),
            fieldParsingError(),
            'VehicleV2.mrmState'
        ),
        position: catchWithLog(
            makeSafeFieldSchema(PositionFieldV2Schema().shape, 'VehicleV2.position').nullish(),
            fieldParsingError(),
            'VehicleV2.position'
        ),
        speedMs: catchWithLog(
            makeSafeFieldSchema(SpeedMsFieldV2Schema().shape, 'VehicleV2.speedMs').nullish(),
            fieldParsingError(),
            'VehicleV2.speedMs'
        ),
    };
    return z.object(safeShape) as unknown as z.ZodType<VehicleV2>;
};

/**
 * Safe version of MissionV2Schema.
 * Each optional field uses makeSafeFieldSchema so that a parse failure degrades
 * the value to null and injects a PARSING_ERROR, without affecting other fields.
 */
export const SafeMissionV2Schema = (): z.ZodType<MissionV2> => {
    const shape = MissionV2Schema().shape;
    // TODO: REST-SUBSCRIPTIONS: the fields below without a `catchWithLog` wrapper are still strict, and
    // any one of them failing throws away the entire mission — which is what makes the stale-mission
    // problem visible in the first place. The three date fields are the worst offenders: they are
    // optional, so the mission does not even need them, yet a malformed one destroys everything.
    // Confirmed to still fail today: an empty `name`, a `timestamp` written with an offset such as
    // "2024-08-22T14:00:00+02:00" instead of "Z", and a `plannedArrivalTime` such as
    // "2024-08-22 14:00:00" with a space instead of a "T".
    // How to fix:
    // 1. Wrap `actualArrivalTime`, `actualStartTime` and `plannedArrivalTime` in
    //    `catchWithLog(shape.<field>, null, 'MissionV2.<field>')`. They are allowed to be missing, so
    //    falling back to null loses nothing and costs nothing.
    // 2. Leave `id` and `vin` strict on purpose. A mission with no identity is not usable, so failing
    //    loudly is correct there.
    // 3. Decide deliberately what `name`, `fleetId`, `timestamp` and `version` should do. A mission is
    //    still useful without a name, so a fallback is probably better than losing the whole object.
    // 4. To verify: add cases to src/helper/tests/v2Schemas.test.ts for each field you wrap, checking
    //    the mission still parses and only that one field degrades.
    const safeShape: ExactShape<MissionV2> = {
        __typename: shape.__typename,
        id: shape.id,
        vin: shape.vin,
        fleetId: shape.fleetId,
        name: shape.name,
        description: catchWithLog(shape.description, '', 'MissionV2.description'),
        timestamp: shape.timestamp,
        events: SafeMissionEventsSchema(),
        version: shape.version,
        route: catchWithLog(shape.route, null, 'MissionV2.route'),
        actualArrivalTime: shape.actualArrivalTime,
        actualStartTime: shape.actualStartTime,
        plannedArrivalTime: shape.plannedArrivalTime,
        progressDetails: catchWithLog(
            makeSafeFieldSchema(MissionProgressDetailsFieldV2Schema().shape, 'MissionV2.progressDetails').nullish(),
            fieldParsingError(),
            'MissionV2.progressDetails'
        ),
        state: catchWithLog(
            makeSafeFieldSchema(MissionStateFieldV2Schema().shape, 'MissionV2.state').nullish(),
            fieldParsingError(),
            'MissionV2.state'
        ),
    };
    return z.object(safeShape) as unknown as z.ZodType<MissionV2>;
};
